<template>
  <v-card outlined tile>
    <v-card-text class="pa-4">
      <v-select
        v-if="!tipoFixo"
        v-model="form.tipo_justificativa"
        :items="tiposJustificativaDisponiveis"
        item-text="text"
        item-value="value"
        label="Forma de justificativa"
        outlined
        @change="limparDados"
      />

      <template v-if="form.tipo_justificativa === 'FOTO'">
        <upload-files
          ref="uploadFotos"
          :allowedFiles="fotosPermitidas"
          :maxFiles="5"
          :validarArquivo="validarFoto"
          uploadFieldName="imagem"
          uploadMethod="uploadImagem"
          uploadPath="gestao-bdgds-erros/evidencias"
          class="mb-0"
          @file:uploaded="adicionarFotos"
        />
        <v-row>
          <v-col v-for="foto in form.arquivos" :key="foto.id" cols="12" md="4">
            <v-card outlined tile>
              <v-card-title class="body-2 py-2">{{ foto.arquivo }}</v-card-title>
              <div style="position: relative">
                <v-img v-if="foto.preview" :src="foto.preview" aspect-ratio="1" />
                <v-responsive v-else aspect-ratio="1" class="align-center d-flex justify-center">
                  <v-skeleton-loader v-if="foto.carregandoPreview" height="100%" tile type="image" width="100%" />
                  <span v-else class="caption grey--text">Não foi possível carregar a imagem.</span>
                </v-responsive>
                <v-btn
                  absolute
                  color="error"
                  dark
                  fab
                  right
                  top
                  x-small
                  :aria-label="`Excluir ${foto.arquivo}`"
                  :loading="fotosExcluindo.includes(foto.id)"
                  style="z-index: 2"
                  @click="excluirFoto(foto)"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <template v-else-if="form.tipo_justificativa === 'RELATORIO'">
        <v-text-field
          :value="form.relatorios.length ? `${form.relatorios.length} relatório(s) selecionado(s)` : ''"
          append-icon="mdi-magnify"
          label="Relatórios"
          outlined
          readonly
          @click="dialogRelatorio = true"
          @click:append="dialogRelatorio = true"
        />
        <evidencias-selecionadas
          v-if="form.relatorios.length"
          :evidencias="form.relatorios.map((relatorio) => ({ id: relatorio.id, titulo: relatorio.nome }))"
          icone="mdi-file-document-outline"
          @remover="removerRelatorio"
        />
      </template>

      <template v-else-if="form.tipo_justificativa === 'EXTRACAO_BDGD'">
        <v-text-field
          :value="form.extracoesBdgd.length ? `${form.extracoesBdgd.length} extração(ões) selecionada(s)` : ''"
          append-icon="mdi-magnify"
          label="Extrações BDGD"
          outlined
          readonly
          @click="abrirSeletorExtracao"
          @click:append="abrirSeletorExtracao"
        />
        <evidencias-selecionadas
          v-if="form.extracoesBdgd.length"
          :evidencias="
            form.extracoesBdgd.map((extracao) => ({
              id: extracao.id,
              titulo: extracao.titulo,
              subtitulo: extracao.tabela || '—'
            }))
          "
          classe-cabecalho="pb-7"
          icone="mdi-database-export-outline"
          @remover="removerExtracao"
        />
      </template>

      <template v-else-if="form.tipo_justificativa === 'CONSULTA_SQL'">
        <div v-for="(consulta, indice) in form.consultasSql" :key="consulta.chave">
          <div v-if="form.consultasSql.length > 1" class="align-center d-flex mb-2">
            <span class="body-2 font-weight-medium">Consulta SQL {{ indice + 1 }}</span>
            <v-spacer />
            <v-btn
              :aria-label="`Remover consulta SQL ${indice + 1}`"
              color="primary"
              icon
              small
              @click="removerConsultaSql(indice)"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-menu v-model="menusDataConsulta[indice]" :close-on-content-click="false" min-width="auto" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatarDataConsulta(consulta.data_consulta)"
                append-icon="mdi-calendar"
                label="Data da consulta"
                outlined
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="consulta.data_consulta" locale="pt-br" @input="fecharMenuDataConsulta(indice)" />
          </v-menu>
          <v-radio-group v-model="consulta.modoConsultaSql" class="mt-0" row>
            <v-radio label="Escrever texto" value="TEXTO" />
            <v-radio label="Importar arquivo(s) SQL" value="ARQUIVOS" />
          </v-radio-group>
          <v-textarea
            v-if="consulta.modoConsultaSql !== 'ARQUIVOS'"
            v-model="consulta.consulta_sql"
            auto-grow
            label="Consulta SQL"
            outlined
            rows="7"
          />
          <template v-else>
            <upload-files
              :ref="`uploadSql${indice}`"
              :allowedFiles="arquivosSqlPermitidos"
              :validarArquivo="validarArquivoSql"
              uploadMethod="uploadSql"
              class="mb-0"
              @file:uploaded="adicionarArquivosSql(indice, $event)"
            />
            <evidencias-selecionadas
              class="mb-4"
              :evidencias="(consulta.arquivos || []).map((arquivo) => ({ id: arquivo.id, titulo: arquivo.arquivo }))"
              icone="mdi-file-code-outline"
              @remover="removerArquivoSql(indice, $event)"
            />
          </template>
        </div>
      </template>

      <template v-else-if="form.tipo_justificativa === 'VALIDACAO_BDGD'">
        <v-text-field
          :value="form.validacoesBdgds.length ? `${form.validacoesBdgds.length} validação(ões) selecionada(s)` : ''"
          append-icon="mdi-magnify"
          label="Validações BDGD"
          outlined
          readonly
          @click="dialogValidacao = true"
          @click:append="dialogValidacao = true"
        />
        <evidencias-selecionadas
          v-if="form.validacoesBdgds.length"
          :evidencias="
            form.validacoesBdgds.map((validacao) => ({
              id: validacao.id,
              titulo: `Validação BDGD #${validacao.id}`,
              subtitulo: validacao.status || '—'
            }))
          "
          icone="mdi-check"
          @remover="removerValidacao"
        />
      </template>
    </v-card-text>

    <dialog-seletor-relatorio
      v-if="dialogRelatorio"
      :activated.sync="dialogRelatorio"
      :selecionados="form.relatorios"
      @selected="selecionarRelatorios"
    />
    <dialog-seletor-extracao-bdgd
      v-if="dialogExtracao"
      :activated.sync="dialogExtracao"
      :bdgd-id="bdgd.id"
      :entidade="entidade"
      :selecionados="form.extracoesBdgd"
      @selected="selecionarExtracoes"
    />
    <dialog-seletor-validacao-bdgd
      v-if="dialogValidacao"
      :activated.sync="dialogValidacao"
      :erro-id="erroId"
      :selecionados="form.validacoesBdgds"
      @selected="selecionarValidacoes"
    />
  </v-card>
</template>

<script>
  import FilesService from '@/services/FilesService.js';
  import UploadFiles from '@/components/general/UploadFiles/Index.vue';
  import DialogSeletorExtracaoBdgd from './DialogSeletorExtracaoBdgd.vue';
  import DialogSeletorRelatorio from './DialogSeletorRelatorio.vue';
  import DialogSeletorValidacaoBdgd from './DialogSeletorValidacaoBdgd.vue';
  import EvidenciasSelecionadas from './EvidenciasSelecionadas.vue';

  const tiposJustificativa = [
    { text: 'Validação de BDGD', value: 'VALIDACAO_BDGD' },
    { text: 'Consulta SQL', value: 'CONSULTA_SQL' },
    { text: 'Imagem(s)', value: 'FOTO' },
    { text: 'Relatório', value: 'RELATORIO' },
    { text: 'Extração BDGD', value: 'EXTRACAO_BDGD' }
  ];

  export default {
    name: 'JustificativaAnalise',

    components: {
      DialogSeletorExtracaoBdgd,
      DialogSeletorRelatorio,
      DialogSeletorValidacaoBdgd,
      EvidenciasSelecionadas,
      UploadFiles
    },

    props: {
      bdgd: {
        type: Object,
        default: () => ({})
      },
      entidade: {
        type: String,
        default: ''
      },
      erroId: {
        type: [Number, String],
        required: true
      },
      fotoDisponivel: {
        type: Boolean,
        default: true
      },
      tiposIndisponiveis: {
        type: Array,
        default: () => []
      },
      tipoFixo: {
        type: Boolean,
        default: false
      },
      value: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        fotosPermitidas: ['jpg', 'jpeg', 'png'],
        arquivosSqlPermitidos: ['sql'],
        fotosExcluindo: [],
        dialogRelatorio: false,
        dialogExtracao: false,
        dialogValidacao: false,
        menusDataConsulta: {},
        form: this.normalizarJustificativa(this.value)
      };
    },

    computed: {
      tiposJustificativaDisponiveis() {
        return tiposJustificativa.map((tipo) => ({
          ...tipo,
          disabled:
            (tipo.value === 'FOTO' && !this.fotoDisponivel) ||
            (this.tiposIndisponiveis.includes(tipo.value) && tipo.value !== this.form.tipo_justificativa)
        }));
      }
    },

    mounted() {
      if (this.form.tipo_justificativa === 'FOTO') this.carregarPreviews(this.form.arquivos);
    },

    beforeUnmount() {
      this.limparPreviews();
    },

    methods: {
      normalizarJustificativa(justificativa) {
        const consultasInformadas = justificativa.consultasSql || [
          {
            chave: justificativa.id || justificativa.chave || `consulta-${Date.now()}`,
            consulta_sql: justificativa.consulta_sql || '',
            data_consulta: justificativa.data_consulta,
            arquivos: justificativa.arquivos || [],
            modoConsultaSql: justificativa.modoConsultaSql
          }
        ];
        const consultasSql = consultasInformadas.map((consulta, indice) => this.normalizarConsultaSql(consulta, indice));

        return {
          chave: justificativa.chave,
          tipo_justificativa: justificativa.tipo_justificativa || null,
          arquivos: justificativa.arquivos || [],
          relatorios: justificativa.relatorios || (justificativa.relatorio ? [justificativa.relatorio] : []),
          extracoesBdgd:
            justificativa.extracoesBdgd ||
            justificativa.extracoes_bdgd ||
            (justificativa.extracao_bdgd ? [justificativa.extracao_bdgd] : []),
          validacoesBdgds:
            justificativa.validacoesBdgds ||
            justificativa.validacoes_bdgds ||
            (justificativa.validacao_bdgd ? [justificativa.validacao_bdgd] : []),
          consultasSql
        };
      },

      normalizarConsultaSql(consulta, indice) {
        const arquivos = consulta.arquivos || [];
        const modoConsultaSql = consulta.modoConsultaSql || (arquivos.length && !consulta.consulta_sql ? 'ARQUIVOS' : 'TEXTO');

        return {
          chave: consulta.chave || `consulta-${indice}-${Date.now()}`,
          modoConsultaSql,
          consulta_sql: consulta.consulta_sql || '',
          data_consulta: consulta.data_consulta ? String(consulta.data_consulta).slice(0, 10) : null,
          arquivos: modoConsultaSql === 'ARQUIVOS' ? arquivos : []
        };
      },

      limparDados() {
        this.limparPreviews();
        this.form = this.normalizarJustificativa({
          chave: this.form.chave,
          tipo_justificativa: this.form.tipo_justificativa
        });
      },

      validarFoto(item) {
        const tiposPermitidos = ['image/jpeg', 'image/png'];

        if (!tiposPermitidos.includes(item.fileObj.type)) return 'Envie uma foto nos formatos JPG, JPEG ou PNG.';
        if (item.fileObj.size > 10 * 1024 * 1024) return 'A foto deve ter no máximo 10 MB.';

        return null;
      },

      async adicionarFotos({ files }) {
        const quantidadeDisponivel = 5 - this.form.arquivos.length;
        const fotosNovas = files
          .filter((arquivo) => !this.form.arquivos.some((item) => item.id === arquivo.id))
          .slice(0, quantidadeDisponivel)
          .map((arquivo) => ({ ...arquivo, carregandoPreview: true, preview: null }));

        this.form.arquivos.push(...fotosNovas);
        await this.carregarPreviews(fotosNovas);
      },

      async carregarPreviews(fotos) {
        for (const foto of fotos) {
          if (foto.preview) continue;

          this.$set(foto, 'carregandoPreview', true);

          try {
            const { data } = await FilesService.download(foto.id, null, this.caminhoImagem(foto));
            const preview = URL.createObjectURL(new Blob([data]));
            const indice = this.form.arquivos.findIndex((item) => item.id === foto.id);

            if (indice === -1) {
              URL.revokeObjectURL(preview);
              continue;
            }

            this.$set(this.form.arquivos[indice], 'preview', preview);
          } catch (error) {
            this.$toast.error('A foto foi enviada, mas não foi possível carregar a prévia.', '', { position: 'topRight' });
          } finally {
            const indice = this.form.arquivos.findIndex((item) => item.id === foto.id);

            if (indice !== -1) this.$set(this.form.arquivos[indice], 'carregandoPreview', false);
          }
        }
      },

      caminhoImagem(foto) {
        return `imagens/gestao-bdgds-erros/evidencias/imagem-${foto.id}.${foto.extensao}`;
      },

      limparPreviews() {
        this.form.arquivos.forEach((foto) => {
          if (foto.preview) URL.revokeObjectURL(foto.preview);
        });
      },

      async excluirFoto(foto) {
        if (this.fotosExcluindo.includes(foto.id)) return;

        this.fotosExcluindo.push(foto.id);

        try {
          await FilesService.delete(foto.id);
          if (foto.preview) URL.revokeObjectURL(foto.preview);
          this.form.arquivos = this.form.arquivos.filter((item) => item.id !== foto.id);
          this.$refs.uploadFotos?.removerArquivoEnviado(foto.id);
        } catch (error) {
          this.$toast.error('Não foi possível excluir a imagem enviada.', '', { position: 'topRight' });
        } finally {
          this.fotosExcluindo = this.fotosExcluindo.filter((id) => id !== foto.id);
        }
      },

      abrirSeletorExtracao() {
        if (!this.bdgd.id) {
          this.$toast.error('Não foi possível identificar a versão da BDGD selecionada.', '', { position: 'topRight' });
          return;
        }

        this.dialogExtracao = true;
      },

      formatarDataConsulta(dataConsulta) {
        if (!dataConsulta) return null;

        const [ano, mes, dia] = String(dataConsulta).slice(0, 10).split('-');

        return ano && mes && dia ? `${dia}/${mes}/${ano}` : dataConsulta;
      },

      validarArquivoSql(item) {
        if (
          !String(item.name || '')
            .toLowerCase()
            .endsWith('.sql')
        ) {
          return 'Envie apenas arquivos no formato SQL (.sql).';
        }

        if (item.fileObj.size > 5 * 1024 * 1024) return 'Cada arquivo SQL deve ter no máximo 5 MB.';

        return null;
      },

      adicionarArquivosSql(indice, { files }) {
        const consulta = this.form.consultasSql[indice];

        files.forEach((arquivo) => {
          if (!consulta.arquivos.some((item) => item.id === arquivo.id)) consulta.arquivos.push(arquivo);
        });
      },

      removerArquivoSql(indice, arquivoId) {
        const consulta = this.form.consultasSql[indice];

        consulta.arquivos = consulta.arquivos.filter((arquivo) => arquivo.id !== arquivoId);

        const upload = this.$refs[`uploadSql${indice}`];
        const componente = Array.isArray(upload) ? upload[0] : upload;

        componente?.removerArquivoEnviado(arquivoId);
      },

      removerConsultaSql(indice) {
        this.form.consultasSql.splice(indice, 1);
      },

      fecharMenuDataConsulta(indice) {
        this.$set(this.menusDataConsulta, indice, false);
      },

      selecionarRelatorios(relatorios) {
        this.dialogRelatorio = false;
        this.form.relatorios = (Array.isArray(relatorios) ? relatorios : [relatorios]).filter(Boolean);
      },

      removerRelatorio(relatorioId) {
        this.form.relatorios = this.form.relatorios.filter((relatorio) => relatorio.id !== relatorioId);
      },

      selecionarExtracoes(extracoes) {
        this.dialogExtracao = false;
        this.form.extracoesBdgd = (Array.isArray(extracoes) ? extracoes : [extracoes]).filter(Boolean);
      },

      removerExtracao(extracaoId) {
        this.form.extracoesBdgd = this.form.extracoesBdgd.filter((extracao) => extracao.id !== extracaoId);
      },

      selecionarValidacoes(validacoes) {
        this.dialogValidacao = false;
        this.form.validacoesBdgds = (Array.isArray(validacoes) ? validacoes : [validacoes]).filter(Boolean);
      },

      removerValidacao(validacaoId) {
        this.form.validacoesBdgds = this.form.validacoesBdgds.filter((validacao) => validacao.id !== validacaoId);
      }
    },

    watch: {
      form: {
        deep: true,
        handler() {
          this.$emit('input', { ...this.form, arquivos: [...this.form.arquivos] });
        }
      }
    }
  };
</script>
